// Drawer de drill-down: mostra TODAS as APIs externas que disparamos pra
// um provider (mock/celcoin/stripe/mercadopago) + TODOS os webhooks que
// recebemos de volta. LÃª de `provider-docs.ts` â€” fonte de verdade.
//
// Aberto a partir dos cards de gateway em PaymentsConfigView (botÃ£o "Ver
// integraÃ§Ã£o"). ConteÃºdo:
//   - Auth + base URLs + link pra docs oficiais
//   - Lista de APIs com mÃ©todo/path/status badge/quando dispara/exemplo
//     de request + response em JSON expandÃ­vel
//   - Lista de webhooks com event/effect/status badge/payload de exemplo
//
// Status badges:
//   implemented  â†’ verde   (cobertura ok)
//   validating   â†’ Ã¢mbar   (caminho chutado, validar com docs reais)
//   skeleton     â†’ Ã¢mbar   (estrutura existe mas nÃ£o testada em prod)
//   not_supportedâ†’ cinza   (declaramos mas nÃ£o implementamos)

import { useState } from "preact/hooks";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Webhook as WebhookIcon,
  X,
  Zap
} from "lucide-react";
import type {
  ApiCallDoc,
  ProviderDoc,
  WebhookDoc
} from "./data/provider-docs";

interface Props {
  provider: ProviderDoc | null;
  onClose: () => void;
}

const STATUS_META: Record<
  ApiCallDoc["status"],
  { label: string; tone: "ok" | "warn" | "muted" }
> = {
  implemented: { label: "Implementado", tone: "ok" },
  validating: { label: "Validando docs", tone: "warn" },
  skeleton: { label: "Esqueleto", tone: "warn" },
  not_supported: { label: "NÃ£o implementado", tone: "muted" }
};

function StatusBadge({ status }: { status: ApiCallDoc["status"] }) {
  const meta = STATUS_META[status];
  const tone = meta.tone;
  const cls =
    tone === "ok"
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
      : tone === "warn"
        ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
        : "border-border bg-muted text-muted-foreground";
  return (
    <span
      class={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${cls}`}
    >
      {meta.label}
    </span>
  );
}

function MethodPill({ method }: { method: ApiCallDoc["method"] }) {
  const color: Record<ApiCallDoc["method"], string> = {
    GET: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
    POST: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    PUT: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    PATCH: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    DELETE: "bg-red-500/15 text-red-700 dark:text-red-400"
  };
  return (
    <span
      class={`shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] font-bold ${color[method]}`}
    >
      {method}
    </span>
  );
}

function JsonBlock({ value, label }: { value: object; label: string }) {
  const pretty = JSON.stringify(value, null, 2);
  async function copy() {
    try {
      await navigator.clipboard.writeText(pretty);
    } catch {
      /* noop */
    }
  }
  return (
    <div class="rounded-md border border-border bg-muted/40">
      <div class="flex items-center justify-between gap-2 border-b border-border px-2 py-1">
        <span class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <button
          type="button"
          onClick={copy}
          class="rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-background hover:text-foreground"
        >
          Copiar
        </button>
      </div>
      <pre class="overflow-x-auto px-2 py-2 font-mono text-[11px] leading-relaxed">
        {pretty}
      </pre>
    </div>
  );
}

function ApiCallItem({ call }: { call: ApiCallDoc }) {
  const [open, setOpen] = useState(false);
  const hasBody = call.requestExample || call.responseExample || call.notes;
  return (
    <div class="rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        class="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-muted/30"
      >
        {hasBody ? (
          open ? (
            <ChevronDown className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          )
        ) : (
          <span class="size-3.5 shrink-0" />
        )}
        <div class="min-w-0 flex-1 space-y-1">
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-sm font-semibold">{call.label}</span>
            <StatusBadge status={call.status} />
          </div>
          <div class="flex flex-wrap items-center gap-2 text-xs">
            <MethodPill method={call.method} />
            <code class="break-all rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">
              {call.externalPath}
            </code>
          </div>
          <div class="text-[11px] text-muted-foreground">{call.when}</div>
        </div>
      </button>
      {open && hasBody && (
        <div class="space-y-2 border-t border-border px-3 py-2.5">
          <div class="text-[10px] text-muted-foreground">
            <span class="font-medium">Dispara em:</span>{" "}
            <code class="break-all rounded bg-muted px-1 font-mono">
              {call.ourCallsite}
            </code>
          </div>
          {call.requestExample && (
            <JsonBlock value={call.requestExample} label="Request body" />
          )}
          {call.responseExample && (
            <JsonBlock value={call.responseExample} label="Response" />
          )}
          {call.notes && (
            <div class="rounded-md border border-amber-500/30 bg-amber-500/5 px-2 py-1.5 text-[11px] text-amber-800 dark:text-amber-300">
              {call.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WebhookItem({ wh }: { wh: WebhookDoc }) {
  const [open, setOpen] = useState(false);
  const hasBody = !!wh.payloadExample;
  return (
    <div class="rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        class="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-muted/30"
      >
        {hasBody ? (
          open ? (
            <ChevronDown className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          )
        ) : (
          <span class="size-3.5 shrink-0" />
        )}
        <div class="min-w-0 flex-1 space-y-1">
          <div class="flex flex-wrap items-center gap-2">
            <code class="break-all rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-semibold">
              {wh.event}
            </code>
            <StatusBadge status={wh.status} />
          </div>
          <div class="text-[11px] text-muted-foreground">{wh.effect}</div>
        </div>
      </button>
      {open && hasBody && wh.payloadExample && (
        <div class="space-y-2 border-t border-border px-3 py-2.5">
          <JsonBlock value={wh.payloadExample} label="Payload recebido" />
        </div>
      )}
    </div>
  );
}

export function ProviderDocsDrawer({ provider, onClose }: Props) {
  if (!provider) return null;
  const apiCount = provider.apiCalls.length;
  const whCount = provider.webhooks.filter(
    (w) => w.status !== "not_supported"
  ).length;

  return (
    <div
      class="fixed inset-0 z-[80] flex justify-end bg-black/40"
      onClick={onClose}
    >
      <div
        class="flex h-full w-full max-w-xl flex-col overflow-hidden border-l border-border bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-start gap-3 border-b border-border px-5 py-4">
          <div class="min-w-0 flex-1">
            <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              IntegraÃ§Ã£o externa
            </div>
            <h2 class="mt-0.5 text-lg font-bold">{provider.label}</h2>
            <div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
              <span>
                <span class="font-medium">Auth:</span>{" "}
                <code class="rounded bg-muted px-1 font-mono">
                  {provider.auth}
                </code>
              </span>
              {provider.docsUrl && (
                <a
                  href={provider.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  Docs oficiais <ExternalLink className="size-3" />
                </a>
              )}
            </div>
            {provider.authNotes && (
              <p class="mt-2 text-[11px] text-muted-foreground">
                {provider.authNotes}
              </p>
            )}
            {(provider.sandboxBaseUrl || provider.prodBaseUrl) && (
              <div class="mt-2 grid gap-1 text-[11px]">
                {provider.sandboxBaseUrl && (
                  <div>
                    <span class="font-medium text-muted-foreground">
                      Sandbox:
                    </span>{" "}
                    <code class="break-all rounded bg-muted px-1 font-mono">
                      {provider.sandboxBaseUrl}
                    </code>
                  </div>
                )}
                {provider.prodBaseUrl && (
                  <div>
                    <span class="font-medium text-muted-foreground">
                      ProduÃ§Ã£o:
                    </span>{" "}
                    <code class="break-all rounded bg-muted px-1 font-mono">
                      {provider.prodBaseUrl}
                    </code>
                  </div>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            class="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <section>
            <div class="mb-2 flex items-center gap-2">
              <Zap className="size-4 text-violet-600" />
              <h3 class="text-sm font-bold">APIs que chamamos</h3>
              <span class="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                {apiCount}
              </span>
            </div>
            {apiCount === 0 ? (
              <div class="rounded-md border border-dashed border-border bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground">
                Nenhuma chamada externa documentada.
              </div>
            ) : (
              <div class="space-y-2">
                {provider.apiCalls.map((call, i) => (
                  <ApiCallItem key={i} call={call} />
                ))}
              </div>
            )}
          </section>

          <section>
            <div class="mb-2 flex items-center gap-2">
              <WebhookIcon className="size-4 text-emerald-600" />
              <h3 class="text-sm font-bold">Webhooks que recebemos</h3>
              <span class="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                {whCount}
              </span>
            </div>
            {provider.webhooks.length === 0 ? (
              <div class="rounded-md border border-dashed border-border bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground">
                Nenhum webhook documentado.
              </div>
            ) : (
              <div class="space-y-2">
                {provider.webhooks.map((wh, i) => (
                  <WebhookItem key={i} wh={wh} />
                ))}
              </div>
            )}
          </section>

          <section class="rounded-md border border-dashed border-border bg-muted/20 p-3 text-[11px] text-muted-foreground">
            <div class="font-bold text-foreground">
              Como manter este painel atualizado
            </div>
            <p class="mt-1">
              Ao portar uma nova chamada/webhook desse provider, edite{" "}
              <code class="rounded bg-muted px-1 font-mono">
                apps/manager-new/src/data/provider-docs.ts
              </code>{" "}
              e ajuste o <code class="rounded bg-muted px-1">status</code> de
              cada item â€” Ã© a fonte de verdade visÃ­vel pelo admin.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
